import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const iOS = /ipad|iphone|ipod/.test(userAgent);
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                        (window.navigator as any).standalone === true;
      
      setIsIOS(iOS);
      setIsStandalone(standalone);
    };

    checkDevice();

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Only show banner if not already installed and user hasn't dismissed it recently
      const dismissed = localStorage.getItem('pwa-banner-dismissed');
      const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
      
      if (!dismissed || dismissedTime < oneDayAgo) {
        setShowBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show banner if not in standalone mode and not recently dismissed
    if (isIOS && !isStandalone) {
      const dismissed = localStorage.getItem('pwa-banner-dismissed-ios');
      const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
      const threeDaysAgo = Date.now() - (3 * 24 * 60 * 60 * 1000);
      
      if (!dismissed || dismissedTime < threeDaysAgo) {
        setShowBanner(true);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isIOS, isStandalone]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        toast.success('App installed successfully!');
      }
      
      setDeferredPrompt(null);
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    if (isIOS) {
      localStorage.setItem('pwa-banner-dismissed-ios', Date.now().toString());
    } else {
      localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
    }
    setShowBanner(false);
  };

  if (!showBanner || isStandalone) {
    return null;
  }

  return (
    <Card className="fixed bottom-4 left-4 right-4 z-50 p-4 border-primary/20 bg-gradient-to-r from-primary/5 to-secondary/5 backdrop-blur-sm">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            {isIOS ? <Smartphone className="w-5 h-5 text-primary" /> : <Download className="w-5 h-5 text-primary" />}
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm mb-1">
            {isIOS ? 'Add to Home Screen' : 'Install LuvALL App'}
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            {isIOS 
              ? 'Tap the share button and select "Add to Home Screen" for better notifications and app experience.'
              : 'Install our app for faster access, better notifications, and offline support.'
            }
          </p>
          
          <div className="flex gap-2">
            {!isIOS && deferredPrompt && (
              <Button
                size="sm"
                onClick={handleInstall}
                className="text-xs h-8"
              >
                Install App
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDismiss}
              className="text-xs h-8"
            >
              {isIOS ? 'Got it' : 'Maybe later'}
            </Button>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="flex-shrink-0 w-8 h-8 p-0"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
}